import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Form, Row, Col, Card } from "react-bootstrap";
import api from "../../api/axios";
import QRScannerModal from "./QRScannerModal";
import { useAppToast } from "../Common/Toast/AppToastProvider";

const QRAdminView = () => {
  const { showToast } = useAppToast();
  const [activeQRs, setActiveQRs] = useState([]);
  const [historyQRs, setHistoryQRs] = useState([]);
  const [claimCode, setClaimCode] = useState("");
  const [query, setQuery] = useState("");

  const [loadingData, setLoadingData] = useState(true);
  const [loadingConsume, setLoadingConsume] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const [activePage, setActivePage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const business = JSON.parse(localStorage.getItem("business") || "null");
  const businessId = business?.id;

  const fetchActive = async () => {
    if (!businessId) return;
    const res = await api.get(`/qr/active/${businessId}`);
    setActiveQRs(res.data.claims || []);
  };

  const fetchHistory = async () => {
    if (!businessId) return;
    const res = await api.get(`/qr/history/${businessId}`);
    setHistoryQRs(res.data.consumptions || []);
  };

  const loadData = async () => {
    if (!businessId) {
      setLoadingData(false);
      return;
    }
    try {
      setLoadingData(true);
      await Promise.all([fetchActive(), fetchHistory()]);
    } catch (err) {
      console.error("Error cargando datos de QR", err);
      showToast("No se pudo cargar la gestion de QR.", "danger", "Error");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [businessId]);

  const consumeQr = async (code) => {
    if (!code) return;
    setLoadingConsume(true);
    try {
      await api.post(`/qr/consume/${code}`);
      await loadData();
      setClaimCode("");
      showToast("QR consumido correctamente.", "success", "Listo");
    } catch (err) {
      console.error(err);
      showToast("No se pudo consumir el QR.", "danger", "Error");
    } finally {
      setLoadingConsume(false);
    }
  };

  const sendEmail = async (code) => {
    try {
      await api.post(`/qr/send/${code}`);
      showToast("Correo enviado correctamente.", "success", "Listo");
    } catch (e) {
      console.error(e);
      showToast("No se pudo enviar el correo.", "danger", "Error");
    }
  };

  const handleScanResult = (scannedCode) => {
    const code = scannedCode.includes("BEN-") ? scannedCode.split("/").pop() : scannedCode;
    setClaimCode(code);
    consumeQr(code);
  };

  const activeFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeQRs;
    return activeQRs.filter(
      (i) =>
        i.claim_code?.toLowerCase().includes(q) ||
        i.product?.name?.toLowerCase().includes(q) ||
        String(i.user_id).includes(q)
    );
  }, [activeQRs, query]);

  const historyFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return historyQRs;
    return historyQRs.filter(
      (i) =>
        i.claim_code?.toLowerCase().includes(q) ||
        i.product?.name?.toLowerCase().includes(q) ||
        String(i.user_id).includes(q)
    );
  }, [historyQRs, query]);

  useEffect(() => {
    setActivePage(1);
    setHistoryPage(1);
  }, [query, pageSize]);

  const activeTotalPages = Math.max(1, Math.ceil(activeFiltered.length / pageSize));
  const historyTotalPages = Math.max(1, Math.ceil(historyFiltered.length / pageSize));

  const activeStart = (activePage - 1) * pageSize;
  const historyStart = (historyPage - 1) * pageSize;

  const activeItems = activeFiltered.slice(activeStart, activeStart + pageSize);
  const historyItems = historyFiltered.slice(historyStart, historyStart + pageSize);

  if (loadingData) {
    const skeletonRows = Array.from({ length: 5 });
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div style={{ height: 28, width: 170, background: "#e5e7eb", borderRadius: 6 }} />
          <div style={{ height: 36, width: 110, background: "#e5e7eb", borderRadius: 999 }} />
        </div>

        <Card className="p-3 mb-4 shadow-sm">
          <Row className="g-2">
            <Col md={5}>
              <div style={{ height: 38, background: "#e5e7eb", borderRadius: 6 }} />
            </Col>
            <Col md={2}>
              <div style={{ height: 38, background: "#e5e7eb", borderRadius: 6 }} />
            </Col>
            <Col md={2}>
              <div style={{ height: 38, background: "#e5e7eb", borderRadius: 6 }} />
            </Col>
            <Col md={3}>
              <div style={{ height: 38, background: "#e5e7eb", borderRadius: 6 }} />
            </Col>
            <Col md={3}>
              <div style={{ height: 38, background: "#e5e7eb", borderRadius: 6 }} />
            </Col>
          </Row>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body>
            {skeletonRows.map((_, idx) => (
              <div
                key={idx}
                style={{
                  display: "grid",
                  gridTemplateColumns: "0.5fr 1.5fr 1fr 1.2fr 1.5fr 1.2fr",
                  gap: "0.75rem",
                  alignItems: "center",
                  padding: "0.55rem 0",
                  borderBottom: idx === skeletonRows.length - 1 ? "none" : "1px solid #e5e7eb",
                }}
              >
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 30, background: "#e5e7eb", borderRadius: 999 }} />
              </div>
            ))}
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Gestion de QR</h2>
        <Button variant="outline-secondary" onClick={loadData}>
          Actualizar
        </Button>
      </div>

      <Card className="p-3 mb-4 shadow-sm">
        <Row className="g-2">
          <Col md={5}>
            <Form.Control
              type="text"
              placeholder="Ingresar codigo para consumir..."
              value={claimCode}
              onChange={(e) => setClaimCode(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Button
              variant="primary"
              className="w-100"
              disabled={!claimCode || loadingConsume}
              onClick={() => consumeQr(claimCode)}
            >
              {loadingConsume ? "Consumiendo..." : "Consumir"}
            </Button>
          </Col>
          <Col md={2}>
            <Button variant="secondary" className="w-100" onClick={() => setShowScanner(true)}>
              Escanear
            </Button>
          </Col>
          <Col md={3}>
            <Form.Control
              type="text"
              placeholder="Buscar en tablas..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={10}>10 por pagina</option>
              <option value={20}>20 por pagina</option>
              <option value={30}>30 por pagina</option>
            </Form.Select>
          </Col>
        </Row>
      </Card>

      <QRScannerModal show={showScanner} onHide={() => setShowScanner(false)} onScan={handleScanResult} />

      <h4>QR Activos</h4>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Codigo</th>
            <th>Usuario</th>
            <th>Beneficio</th>
            <th>Fecha Reclamo</th>
            <th>Accion</th>
          </tr>
        </thead>
        <tbody>
          {activeItems.map((qr, i) => (
            <tr key={qr.id}>
              <td>{activeStart + i + 1}</td>
              <td>{qr.claim_code}</td>
              <td>{qr.user_id}</td>
              <td>{qr.product?.name}</td>
              <td>{new Date(qr.created_at).toLocaleString()}</td>
              <td>
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => consumeQr(qr.claim_code)}
                  disabled={loadingConsume}
                >
                  Consumir
                </Button>
                <Button size="sm" variant="outline-primary" className="ms-2" onClick={() => sendEmail(qr.claim_code)}>
                  Enviar mail
                </Button>
              </td>
            </tr>
          ))}
          {activeItems.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                No hay QR activos.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <div className="d-flex justify-content-end gap-2 mb-4">
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={activePage <= 1}
          onClick={() => setActivePage((p) => p - 1)}
        >
          Anterior
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={activePage >= activeTotalPages}
          onClick={() => setActivePage((p) => p + 1)}
        >
          Siguiente
        </Button>
      </div>

      <h4 className="mt-4">Historial de QR Consumidos</h4>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Codigo</th>
            <th>Usuario</th>
            <th>Beneficio</th>
            <th>Fecha Uso</th>
          </tr>
        </thead>
        <tbody>
          {historyItems.map((qr, i) => (
            <tr key={qr.usage_id}>
              <td>{historyStart + i + 1}</td>
              <td>{qr.claim_code}</td>
              <td>{qr.user_id}</td>
              <td>{qr.product?.name}</td>
              <td>{new Date(qr.used_at).toLocaleString()}</td>
            </tr>
          ))}
          {historyItems.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-muted">
                Todavia no hay consumos registrados.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <div className="d-flex justify-content-end gap-2">
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={historyPage <= 1}
          onClick={() => setHistoryPage((p) => p - 1)}
        >
          Anterior
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={historyPage >= historyTotalPages}
          onClick={() => setHistoryPage((p) => p + 1)}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
};

export default QRAdminView;
